// Owns the variable-side-panel hover preview: when the user hovers a
// variable name in the side panel, the canvas pans (and zoom-fits if
// multiple) to all nodes referencing that variable, then restores the
// prior view on hover-leave. Also exposes pinCurrentHoverView() — call
// it when the user commits to the current view (clicks a node, pans the
// canvas) so the eventual hover-leave does NOT animate back.
//
// Two refs are owned internally:
//   - preHoverViewRef: snapshot of pan/zoom captured before the first
//     hover, used as the baseline to restore to. Survives row→row
//     hand-offs because it's only cleared by the deferred timer.
//   - hoverRestoreTimerRef: pending restore-cleanup so a re-hover within
//     the 700ms restore window picks up the correct pre-hover view.

const React = require('react');
const { useRef, useEffect } = React;

function useHoverPreview(deps) {
  var canvasRef = deps.canvasRef;
  var panPosRef = deps.panPosRef;
  var zoomRef = deps.zoomRef;
  var rectByIdRef = deps.rectByIdRef;
  var hoveredVariableName = deps.hoveredVariableName;
  var setHoveredVariableName = deps.setHoveredVariableName;
  var nodeIdsByVariableName = deps.nodeIdsByVariableName;
  var autoPanAnimateTo = deps.autoPanAnimateTo;
  // Optional: a single node id to focus directly (e.g. hovering a Run Log
  // line). Drives the exact same pan/zoom as a variable hover. null = inactive.
  var focusNodeId = deps.focusNodeId || null;

  // Resolve the node ids to focus this pass. A direct focusNodeId wins;
  // otherwise fall back to the variable's referencing nodes.
  var activeKey = focusNodeId || hoveredVariableName;
  var activeIds = focusNodeId
    ? [focusNodeId]
    : (hoveredVariableName ? (nodeIdsByVariableName[hoveredVariableName] || []) : []);

  var preHoverViewRef = useRef(null);
  var hoverRestoreTimerRef = useRef(null);

  // Called when the user commits to the current zoomed-in view (clicks a
  // node, pans the canvas, etc.). Drops the saved pre-hover view so the
  // eventual hover-leave does NOT animate back, and clears the highlight.
  function pinCurrentHoverView() {
    if (!hoveredVariableName && !preHoverViewRef.current) return;
    if (hoverRestoreTimerRef.current) {
      clearTimeout(hoverRestoreTimerRef.current);
      hoverRestoreTimerRef.current = null;
    }
    preHoverViewRef.current = null;
    setHoveredVariableName(null);
  }

  useEffect(function() {
    var canvas = canvasRef.current;
    if (!canvas) return;

    // Any new hover transition cancels a pending baseline-cleanup so the
    // saved view stays valid across rapid hover↔leave cycles.
    if (hoverRestoreTimerRef.current) {
      clearTimeout(hoverRestoreTimerRef.current);
      hoverRestoreTimerRef.current = null;
    }

    if (!activeKey) {
      // Hover ended — animate back to the saved baseline view. Defer the
      // baseline cleanup until AFTER the restore animation finishes, so
      // a re-hover within the 700ms restore window picks up the correct
      // pre-hover view (not the mid-animation zoom we'd otherwise capture
      // from zoomRef).
      var saved = preHoverViewRef.current;
      if (!saved) return;
      autoPanAnimateTo(saved.x, saved.y, saved.zoom, 700);
      hoverRestoreTimerRef.current = setTimeout(function() {
        hoverRestoreTimerRef.current = null;
        preHoverViewRef.current = null;
      }, 720);
      return;
    }

    var ids = activeIds;
    if (ids.length === 0) return;

    if (!preHoverViewRef.current) {
      preHoverViewRef.current = {
        x: panPosRef.current.x,
        y: panPosRef.current.y,
        zoom: zoomRef.current
      };
    }

    var canvasW = canvas.clientWidth;
    var canvasH = canvas.clientHeight;
    var targetZoom, targetX, targetY;

    // Compute the visible canvas region — the canvas minus any floating
    // panels overlapping it. Two real occluders today:
    //   1. .vars-panel on the LEFT (~300px when expanded, narrow when
    //      collapsed) — measured live.
    //   2. .sidebar.sidebar-open on the RIGHT (open node-params panel,
    //      wider when .sidebar-wide). The sidebar lives outside .canvas-area
    //      in the layout, but on smaller layouts or when the canvas's
    //      bounding box overlaps the sidebar viewport area, we still need
    //      to bias the centering left so the bbox isn't pushed under the
    //      sidebar.
    // Centering the bbox at (visibleLeft + visibleRight)/2 keeps the
    // leftmost highlighted node clear of the variables panel and the
    // rightmost clear of the params sidebar.
    var canvasBox = canvas.getBoundingClientRect();
    var visibleLeftEdge = 0;
    var visibleRightEdge = canvasW;
    var varsPanelEl = document.querySelector('.vars-panel');
    if (varsPanelEl) {
      var varsBox = varsPanelEl.getBoundingClientRect();
      // Only subtract the panel when it actually overlaps the canvas (the
      // panel is positioned inside .canvas-area, so this is normally true).
      if (varsBox.right > canvasBox.left && varsBox.left < canvasBox.right) {
        visibleLeftEdge = Math.max(0, varsBox.right - canvasBox.left + 16);
      }
    }
    var sidebarEl = document.querySelector('.sidebar.sidebar-open');
    if (sidebarEl) {
      var sidebarBox = sidebarEl.getBoundingClientRect();
      if (sidebarBox.left < canvasBox.right && sidebarBox.right > canvasBox.left) {
        visibleRightEdge = Math.min(canvasW, sidebarBox.left - canvasBox.left - 16);
      }
    }
    var visibleW = Math.max(visibleRightEdge - visibleLeftEdge, 200);
    var visibleCx = visibleLeftEdge + visibleW / 2;

    // Single node = keep baseline zoom and just pan. Multi-node = fit the
    // bbox with 140px padding, but only zoom OUT — never below the user's
    // baseline zoom (so we don't pull them way back when nodes already fit
    // in view) and never below MIN_ZOOM_FIT (a hard floor to prevent the
    // "tiny dots in the corner" effect when the bbox spans the whole graph).
    // Centering uses visibleCx instead of canvasW/2 so the highlighted
    // nodes don't disappear under the variables panel or the right-side
    // editor.
    //
    // IMPORTANT: read the baseline from preHoverViewRef, not zoomRef. On a
    // row→row hand-off the canvas is still showing the previous hover's
    // (zoomed-out) target, so zoomRef would lock us in at that smaller
    // zoom and we'd never come back up. preHoverViewRef holds the zoom
    // the user had BEFORE any hover started, which is the right reference.
    var baselineZoom = preHoverViewRef.current ? preHoverViewRef.current.zoom : zoomRef.current;
    var MIN_ZOOM_FIT = 0.55;

    if (ids.length === 1) {
      // Single-node target: pan to the node at the baseline zoom. We can't
      // use the live element rect here because at the previous hover's zoom
      // it would map back through the wrong scale — go through world coords.
      var singleRect = rectByIdRef.current[ids[0]];
      if (!singleRect) return;
      targetZoom = baselineZoom;
      targetX = visibleCx - singleRect.cx * targetZoom;
      targetY = canvasH / 2 - singleRect.cy * targetZoom;
    } else {
      var minLeft = Infinity, minTop = Infinity, maxRight = -Infinity, maxBottom = -Infinity;
      var validCount = 0;
      ids.forEach(function(id) {
        var r = rectByIdRef.current[id];
        if (!r) return;
        minLeft   = Math.min(minLeft,   r.left);
        minTop    = Math.min(minTop,    r.top);
        maxRight  = Math.max(maxRight,  r.right);
        maxBottom = Math.max(maxBottom, r.bottom);
        validCount++;
      });
      if (validCount === 0) return;
      var bboxCx = (minLeft + maxRight) / 2;
      var bboxCy = (minTop  + maxBottom) / 2;
      var padding = 140;
      var bboxW = maxRight - minLeft;
      var bboxH = maxBottom - minTop;
      var fitZoom = Math.min(
        (visibleW - padding * 2) / bboxW,
        (canvasH - padding * 2) / bboxH,
        1.5
      );
      // If the cluster already fits at the baseline zoom, keep it — only
      // pan. Otherwise zoom out just enough to fit, capped at MIN_ZOOM_FIT.
      targetZoom = Math.min(baselineZoom, Math.max(fitZoom, MIN_ZOOM_FIT));
      targetX = visibleCx - bboxCx * targetZoom;
      targetY = canvasH / 2 - bboxCy * targetZoom;
    }

    autoPanAnimateTo(targetX, targetY, targetZoom, 750);
  }, [activeKey, nodeIdsByVariableName, focusNodeId]);

  return { pinCurrentHoverView: pinCurrentHoverView };
}

module.exports = useHoverPreview;
